import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Search, ShieldCheck, Star } from "lucide-react";
import Seo from "../components/Seo";
import Marquee from "../components/Marquee";
import FaqAccordion from "../components/FaqAccordion";
import ServiceCard from "../components/ServiceCard";
import ArticleCard from "../components/ArticleCard";
import { Reveal, ChapterHeading, Eyebrow } from "../components/Reveal";
import {
  SITE,
  SERVICES,
  LOCATIONS,
  FEATURED_LOCATIONS,
  REGION_ORDER,
  locationsByRegion,
  findLocation,
  HOME_FAQS,
  IMAGES,
  INDUSTRY_LINKS,
} from "../data/site";
import ARTICLES from "../data/articles";
import { TESTIMONIALS } from "../data/testimonials";

const STATS = [
  { value: "120+", label: "England towns & cities covered" },
  { value: "17", label: "Specialist security services" },
  { value: "24/7", label: "England command & response" },
  { value: "100%", label: "SIA licensed & BS 7858 vetted" },
];

const BADGES = ["SIA Licensed", "BS 7858 Vetted", "ISO 9001", "£10m Insured"];

function HeroLine({ text, i }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
      >
        {text}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const [query, setQuery] = useState("");
  const [missed, setMissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const xY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [location.hash]);

  const submit = (e) => {
    e.preventDefault();
    const loc = findLocation(query);
    if (loc) {
      navigate(`/locations/${loc.slug}`);
    } else {
      setMissed(true);
      navigate(`/locations?q=${encodeURIComponent(query)}`);
    }
  };

  const latestArticles = [...ARTICLES].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3);

  return (
    <>
      <Seo
        title="Security Guards in England | Manned Guarding & Patrols | Xtreme Protection England"
        description="Security guards, mobile patrols and event security for businesses across England. Tell us your site and hours for a tailored proposal."
        keywords="security guards England, security guards London, security guards Manchester, security guards Birmingham, mobile patrol Leeds"
        canonical={`${SITE.domain}/`}
      />

      <section
        ref={heroRef}
        className="relative min-h-[78vh] flex items-end overflow-hidden tactical-grid"
        data-testid="home-hero"
      >
        <motion.div className="absolute inset-0 -top-24" style={{ y: imgY }}>
          <img
            src={IMAGES.hero.url}
            alt={IMAGES.hero.alt}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/55 to-ink"></div>
        </motion.div>
        <motion.div
          className="absolute -right-32 top-16 font-display font-black text-[38vw] leading-none text-outline select-none pointer-events-none hidden lg:block"
          aria-hidden="true"
          style={{ y: xY }}
        >
          X
        </motion.div>
        <div className="relative w-full max-w-7xl mx-auto px-5 sm:px-8 pb-14 sm:pb-20 pt-24">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <Eyebrow>England Security Operations // SIA Licensed</Eyebrow>
          </motion.div>
          <h1 className="font-display font-black uppercase leading-[0.92] tracking-tight text-[13vw] sm:text-7xl lg:text-8xl text-white">
            <HeroLine text="Security guard" i={0} />
            <HeroLine text="services" i={1} />
            <HeroLine text="across England" i={2} />
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            <p className="mt-7 max-w-2xl text-base sm:text-lg text-gray-300 leading-relaxed">
              From London to Newcastle upon Tyne, Xtreme Protection fields
              SIA-licensed security guards, mobile patrols, CCTV monitoring and
              rapid response teams across{" "}
              <span className="text-amber font-semibold">
                120+ England towns and cities
              </span>{" "}
              — every shift verified, evidenced and accountable.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/locations"
                data-testid="hero-explore-locations-btn"
                className="group flex items-center gap-3 bg-amber text-ink font-display font-bold uppercase tracking-widest text-sm px-7 py-4 hover:bg-white transition-colors duration-300"
              >
                Explore 120+ England Locations
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-view-services-btn"
                className="group flex items-center gap-3 border border-white/25 text-white font-display font-bold uppercase tracking-widest text-sm px-7 py-4 hover:border-amber hover:text-amber transition-colors duration-300"
              >
                View All Security Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section
        id="coverage"
        className="border-b border-white/10 bg-coal/40 tactical-grid"
        data-testid="coverage-checker"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <Eyebrow>Coverage Checker</Eyebrow>
              <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white leading-tight">
                Do we cover your area?
              </h2>
              <p className="mt-4 text-sm sm:text-base text-gray-400 leading-relaxed max-w-md">
                Type in your town or city and land straight on your local
                security page — {LOCATIONS.length} locations across England, 17
                specialist services, one standard of proof.
              </p>
            </div>
            <div>
              <form
                onSubmit={submit}
                className="relative flex border border-white/15 bg-ink focus-within:border-amber transition-colors duration-300"
              >
                <div className="flex items-center pl-4 text-gray-500">
                  <Search className="w-4 h-4" />
                </div>
                <input
                  data-testid="coverage-input"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setMissed(false);
                  }}
                  placeholder="e.g. Leeds, Bristol, Norwich, York…"
                  autoComplete="off"
                  className="flex-1 bg-transparent px-4 py-4 text-sm text-white placeholder-gray-600 outline-none font-body"
                />
                <button
                  type="submit"
                  data-testid="coverage-submit"
                  className="bg-amber text-ink font-display font-bold uppercase tracking-widest text-sm px-6 hover:bg-white transition-colors duration-300"
                >
                  Check
                </button>
              </form>
              {missed && (
                <p data-testid="coverage-miss" className="mt-3 font-mono text-xs uppercase tracking-widest text-amber">
                  Not listed? We still cover you — browse the full directory.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-coal/40" data-testid="home-stats">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal
              key={s.label}
              delay={i * 0.08}
              className={`py-8 px-4 sm:px-8 ${i > 0 ? "border-l border-white/10" : ""}`}
            >
              <div className="font-display font-black text-3xl sm:text-5xl text-amber">
                {s.value}
              </div>
              <div className="mt-2 font-mono text-[10px] sm:text-xs uppercase tracking-widest text-gray-400">
                {s.label}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="chapter-standard">
        <ChapterHeading number="01" label="THE STANDARD" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
              Discipline is the deterrent
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="space-y-5">
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Most security companies ask you to take their word for it. We hand
              you the evidence instead: officers screened to BS 7858 and licensed
              by the SIA, patrols verified by GPS and NFC checkpoints,
              photographic reports stamped to the minute, and a contract manager
              who sits through a performance review with you every month.
            </p>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Whether it is one night guard at a single site or a portfolio
              stretching across England, the process never changes — a free risk
              assessment, straight-talking recommendations, and a written plan we
              are happy to be measured against.
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {BADGES.map((b) => (
                <span
                  key={b}
                  className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-amber border border-amber/30 px-2.5 py-1.5"
                >
                  <ShieldCheck className="w-3 h-3" /> {b}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-24" data-testid="chapter-arsenal">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="02" label="THE ARSENAL" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-3xl">
              17 specialist services. One accountable partner.
            </h2>
            <p className="mt-5 max-w-2xl text-sm sm:text-base text-gray-400 leading-relaxed">
              Manned guarding, door supervision, CCTV monitoring, mobile patrols,
              event security, close protection, K9 units, key holding and more —
              combined into packages shaped by your risk assessment, not a
              brochure.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="home-services-grid">
            {SERVICES.map((svc, i) => (
              <Reveal key={svc.slug} delay={(i % 4) * 0.06}>
                <ServiceCard svc={svc} index={i} prefix="home-service-card" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 sm:py-24" data-testid="chapter-territory">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="03" label="THE TERRITORY" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <Reveal>
              <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
                120+ towns and cities. Nine regions. One command.
              </h2>
              <p className="mt-5 max-w-xl text-sm sm:text-base text-gray-400 leading-relaxed">
                Big-company resources, genuinely local officers. Regional command
                means the guard on your gate knows your streets, your local
                police liaison and your area&apos;s risk profile — with our 24/7
                England command centre behind every shift.
              </p>
              <Link
                to="/locations"
                data-testid="territory-all-locations-btn"
                className="group mt-8 inline-flex items-center gap-3 border border-amber text-amber font-display font-bold uppercase tracking-widest text-sm px-7 py-4 hover:bg-amber hover:text-ink transition-colors duration-300"
              >
                Full England Directory
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="featured-cities-grid">
              {FEATURED_LOCATIONS.map((loc, i) => (
                <Reveal key={loc.slug} delay={(i % 2) * 0.06}>
                  <Link
                    to={`/locations/${loc.slug}`}
                    data-testid={`featured-city-${loc.slug}`}
                    className="group flex items-center justify-between gap-3 border border-white/10 bg-card px-5 py-4 hover:border-amber transition-colors duration-300"
                  >
                    <span>
                      <span className="block font-display font-bold uppercase tracking-wide text-lg text-white group-hover:text-amber transition-colors">
                        Security {loc.name}
                      </span>
                      <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                        {loc.county}
                      </span>
                    </span>
                    <MapPin className="w-4 h-4 text-amber shrink-0" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3" data-testid="region-counts">
            {REGION_ORDER.map((region) => (
              <span key={region} className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                <span className="text-amber">{locationsByRegion(region).length}</span> — {region}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-24" data-testid="chapter-proof">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="04" label="THE PROOF" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-3xl">
              Clients who sleep at night
            </h2>
            <p className="mt-5 max-w-2xl text-sm sm:text-base text-gray-400 leading-relaxed">
              Facilities managers, operations directors and business owners
              across England trust Xtreme Protection with their sites, their
              stock and their people.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.08} className="h-full">
                <figure
                  data-testid={`testimonial-${i}`}
                  className="corner-card bg-card border border-white/10 h-full p-6 flex flex-col hover:border-amber/60 transition-colors duration-300"
                >
                  <div className="flex gap-1" aria-label="5 star review">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className="w-4 h-4 text-amber fill-amber" />
                    ))}
                  </div>
                  <blockquote className="mt-4 text-sm sm:text-base text-gray-300 leading-relaxed flex-1">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-5 font-mono text-[10px] uppercase tracking-widest">
                    <span className="text-white block">
                      {t.role} — {t.sector}
                    </span>
                    <span className="text-gray-500 block mt-1">{t.region}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 sm:py-24" data-testid="chapter-intel">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="05" label="THE INTEL DESK" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-3xl">
              Security intelligence, in plain English
            </h2>
            <p className="mt-5 max-w-2xl text-sm sm:text-base text-gray-400 leading-relaxed">
              Practical guides on guarding costs, SIA licensing, patrol strategy
              and sector security across England — written by the people who run
              the shifts.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-articles-grid">
            {latestArticles.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08}>
                <ArticleCard article={a} prefix="home-article" />
              </Reveal>
            ))}
          </div>
          <Reveal>
            <Link
              to="/resources"
              data-testid="intel-all-articles-btn"
              className="group mt-10 inline-flex items-center gap-3 border border-amber text-amber font-display font-bold uppercase tracking-widest text-sm px-7 py-4 hover:bg-amber hover:text-ink transition-colors duration-300"
            >
              Browse All Guides
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-24" data-testid="chapter-industry">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="06" label="ACCREDITATIONS & INDUSTRY" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white leading-tight max-w-3xl">
              Aligned with England&apos;s security regulators
            </h2>
            <p className="mt-5 max-w-2xl text-sm sm:text-base text-gray-400 leading-relaxed">
              We work to the standards set by the UK&apos;s security industry
              bodies. Verify us — and any provider you compare us against —
              through the official registers below.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl">
              {INDUSTRY_LINKS.map((l) => (
                <a
                  key={l.url}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between gap-3 border border-white/10 bg-card px-5 py-4 hover:border-amber transition-colors duration-300"
                >
                  <span className="font-display font-bold uppercase tracking-wide text-sm text-white group-hover:text-amber transition-colors">
                    {l.name}
                  </span>
                  <ArrowRight className="w-4 h-4 text-amber shrink-0 -rotate-45" />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section id="faq" className="border-t border-white/10 py-16 sm:py-24" data-testid="chapter-answers">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="07" label="THE BRIEFING" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight mb-10">
              Straight answers on security
            </h2>
            <FaqAccordion faqs={HOME_FAQS} testidPrefix="home-faq" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
